import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router';
import { usersApi, setAuthToken } from '../lib/api';
import { useAuth } from '../hooks/useAuth';

export function AcceptInvitePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setUser, setAccount } = useAuth();

  const token = searchParams.get('token') || '';

  const [inviteInfo, setInviteInfo] = useState<Record<string, unknown> | null>(null);
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      setError('Invalid invitation link. Please ask your admin for a new one.');
      setLoadingInfo(false);
      return;
    }
    // Fetch invitation info to personalize the page
    usersApi.getInviteInfo(token)
      .then((res) => setInviteInfo(res.data))
      .catch((err) => setError(err.message || 'Invalid or expired invitation.'))
      .finally(() => setLoadingInfo(false));
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      // Name comes from the invitation record (first_name + last_name set by admin)
      const res = await usersApi.acceptInvite({ token, password, name: 'Invited User' });
      setAuthToken(res.data.token);
      setUser(res.data.user);
      setAccount(res.data.account);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to accept invitation.');
    } finally {
      setLoading(false);
    }
  };

  const firstName = inviteInfo?.first_name || '';

  return (
    <div className="min-h-screen bg-navy-950 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img src="/logo-full.png" alt="RankHelm" className="h-10 mx-auto" />
        </div>

        {/* Card */}
        <div className="bg-navy-900 border border-navy-700 rounded-2xl p-8">
          {loadingInfo ? (
            <div className="text-center py-8">
              <p className="text-slate-400">Loading invitation...</p>
            </div>
          ) : inviteInfo ? (
            <>
              <h2 className="text-xl font-semibold text-white mb-1">
                {firstName ? `Welcome, ${firstName}!` : 'Welcome!'}
              </h2>
              <p className="text-sm text-slate-400 mb-1">
                You've been invited to join RankHelm.
              </p>
              <p className="text-sm text-slate-400 mb-6">
                Create your password below to get started.
              </p>

              {error && (
                <div className="bg-danger/10 border border-danger/30 text-danger text-sm rounded-lg px-4 py-3 mb-5">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1.5">Password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Minimum 8 characters"
                    className="w-full px-4 py-2.5 bg-navy-800 border border-navy-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-helm-500 focus:border-transparent"
                    required
                    minLength={8}
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1.5">Confirm Password</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm your password"
                    className="w-full px-4 py-2.5 bg-navy-800 border border-navy-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-helm-500 focus:border-transparent"
                    required
                    minLength={8}
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-2.5 bg-helm-500 hover:bg-helm-400 text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
                >
                  {loading ? 'Creating account...' : 'Join Team'}
                </button>
              </form>
            </>
          ) : (
            <div className="text-center py-4">
              {error && (
                <div className="bg-danger/10 border border-danger/30 text-danger text-sm rounded-lg px-4 py-3 mb-4">
                  {error}
                </div>
              )}
              <p className="text-slate-400 text-sm mb-4">This invitation link is invalid or has expired.</p>
              <Link to="/login" className="text-helm-400 hover:text-helm-300 text-sm font-medium transition-colors">
                Go to login
              </Link>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-slate-500 mt-6">
          Already have an account?{' '}
          <Link to="/login" className="text-helm-400 hover:text-helm-300 transition-colors">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
